import React from 'react';
import {
    Button,
    ButtonProps,
    ButtonKind,
    ButtonVariant,
    IconButton,
    NetworkOutlined,
} from '@landr/maestro';
import { Sample, InstrumentationOptions } from 'types';
import { RouterLink } from 'components/CommonUi/RouterLink';
import addQueryStringTo from 'helpers/addQueryStringTo';
import { FeatureFlag, RoutePaths } from 'types/enums';
import { useFlag } from 'hooks/useFlag/useFlag';
import { useToLocalizedPath } from 'hooks/useToLocalizedPath/useToLocalizedPath';
import useSelectorButton from './useSelectorButton';

export interface SelectorButtonProps extends ButtonProps {
    sample: Sample;
    instrumentationOptions: InstrumentationOptions;
    onClick?: (event?: React.MouseEvent) => void;
    kind?: ButtonKind;
    variant?: ButtonVariant;
    children?: React.ReactElement | string;
    key?: string | number | undefined;
    label?: string;
}

export const SelectorButton = ({
    children,
    key,
    label,
    sample,
    instrumentationOptions,
    onClick,
    ...props
}: SelectorButtonProps) => {
    const hasPersistentSearchFF = useFlag(FeatureFlag.PersistentSearch);

    const { handleOnClick, labelText } = useSelectorButton({
        sample,
        instrumentationOptions,
        onClick,
        label,
    });

    const toLocalizedPath = useToLocalizedPath();

    const pathname = toLocalizedPath(
        `/${RoutePaths.Recommendations}/${sample.guid}`,
    );
    const to = addQueryStringTo(pathname, hasPersistentSearchFF);

    if (children) {
        return (
            <Button
                as={RouterLink}
                to={to}
                onClick={handleOnClick}
                icon={NetworkOutlined}
                aria-label={labelText}
                {...props}
                key={key}
            >
                {children}
            </Button>
        );
    }

    return (
        <IconButton
            as={RouterLink}
            to={to}
            onClick={handleOnClick}
            icon={NetworkOutlined}
            label={labelText}
            aria-label={labelText}
            {...props}
            key={key}
        />
    );
};
